import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { eventsAPI, activitiesAPI } from '../../services/api';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../contexts/AuthContext';
import EditActivityForm from '../../components/features/Activities/EditActivityForm';
import EditEventForm from '../../components/features/Activities/EditEventForm';
import Countdown from '../../components/shared/Countdown';
import { STORAGE_PATHS } from '../../config/constants';
import 'react-image-crop/dist/ReactCrop.css';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './Activities.css';
import '../../styles/shared/PageHeader.css';
import { errorHandler } from '../../utils/errorHandler';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import AddActivityModal from '../../components/features/Activities/AddActivityModal';
import AddEventModal from '../../components/features/Activities/AddEventModal';
import { getCountryName } from '../../utils/locationUtils';
import EventPartners from '../../components/features/Events/EventPartners';

function Activities() {
    const [activities, setActivities] = useState([]);
    const [message, setMessage] = useState('');
    const [editingActivity, setEditingActivity] = useState(null);
    const [newActivity, setNewActivity] = useState({
        title: '',
        description: '',
        date: '',
        country: '',
        state: '',
        location: '',
        images: []
    });
    const [imageFiles, setImageFiles] = useState([]);
    const [previewImages, setPreviewImages] = useState([]);
    const [showAddActivityForm, setShowAddActivityForm] = useState(false);
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { currentUser, hasRole } = useAuth();
    const isEditor = hasRole('editor');
    const isAdmin = hasRole('admin');
    const isSuperAdmin = hasRole('superAdmin');
    const [showAddEventForm, setShowAddEventForm] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [newEvent, setNewEvent] = useState({
        title: '',
        description: '',
        date: '',
        country: '',
        city: '',
        requirements: '',
        registrationLink: '',
        imageUrl: ''
    });
    const [eventImageFile, setEventImageFile] = useState(null);
    const [previewEventImage, setPreviewEventImage] = useState(null);
    const [pageLoading, setPageLoading] = useState(true);
    const [retryCount, setRetryCount] = useState(0);
    const [showEditActivityForm, setShowEditActivityForm] = useState(false);
    const [showEditEventForm, setShowEditEventForm] = useState(false);
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 20,
        totalPages: 1
    });
    const eventsGridRef = useRef(null);

    useEffect(() => {
        fetchActivitiesAndEvents();
    }, [currentUser, retryCount, pagination.page]);

    const fetchActivitiesAndEvents = async () => {
        try {
            setPageLoading(true);
            setError(null);
            setMessage('');
            await Promise.all([
                fetchActivitiesInternal(),
                fetchUpcomingEventsInternal()
            ]);
        } catch (err) {
            setError(errorHandler.processError(err).message);
        } finally {
            setPageLoading(false);
        }
    };
    
    const fetchActivitiesInternal = async () => {
        try {
            const response = await activitiesAPI.getAllActivities(pagination.page, pagination.limit);
            if (!response || !response.activities) {
                throw new Error('No activities data received');
            }
            const activitiesArray = Array.isArray(response.activities) ? response.activities : [];
            setActivities(activitiesArray);
            if (response.pagination) {
                setPagination(prev => ({ ...prev, ...response.pagination }));
            }
        } catch (err) {
            setError(prevErr => prevErr || errorHandler.processError(err).message);
            setActivities([]); 
        }
    };

    const fetchUpcomingEventsInternal = async () => {
        try {
            const response = await eventsAPI.getUpcomingEvents();
            if (!response) {
                throw new Error('No upcoming events data received');
            }
            const eventsArray = Array.isArray(response) ? response : [];
            setUpcomingEvents(eventsArray);
        } catch (err) {
            setError(prevErr => prevErr || errorHandler.processError(err).message);
            setUpcomingEvents([]);
        }
    };

    const handleActivityFormInputChange = (e) => {
        const { name, value } = e.target;
        setNewActivity(prev => ({
            ...prev,
            [name]: value
        }));
        setError(null);
    };

    const handleActivityFileChange = (e) => {
        const files = Array.from(e.target.files);
        const maxFiles = 10;
        const maxSizeInBytes = 5 * 1024 * 1024; // 5MB

        if (files.length + imageFiles.length > maxFiles) {
            alert(`You can only upload up to ${maxFiles} images`);
            return;
        }

        const validFiles = files.filter(file => {
            if (file.size > maxSizeInBytes) {
                alert(`File ${file.name} exceeds 5MB limit`);
                return false;
            }
            return true;
        });

        setImageFiles(prev => [...prev, ...validFiles]);
        const newPreviews = validFiles.map(file => URL.createObjectURL(file));
        setPreviewImages(prev => [...prev, ...newPreviews]);
        setError(null);
    };

    const handleActivityCountryChange = (e) => {
        const countryCode = e.target.value;
        setNewActivity(prev => ({
            ...prev,
            country: countryCode,
            state: '',
            location: ''
        }));
        setError(null);
    };

    const handleActivityStateChange = (e) => {
        const stateCode = e.target.value;
        setNewActivity(prev => ({
            ...prev,
            state: stateCode,
            location: ''
        }));
        setError(null);
    };

    const handleActivityRemoveImage = (index) => {
        setImageFiles(prev => prev.filter((_, i) => i !== index));
        setPreviewImages(prev => {
            const updatedPreviews = prev.filter((_, i) => i !== index);
            if (previewImages[index] && typeof previewImages[index] === 'string' && previewImages[index].startsWith('blob:')) {
                 URL.revokeObjectURL(previewImages[index]);
            }
            return updatedPreviews;
        });
    };

    const resetActivityForm = () => {
        setNewActivity({ title: '', description: '', date: '', country: '', state: '', location: '', images: [] });
        previewImages.forEach(URL.revokeObjectURL);
        setImageFiles([]);
        setPreviewImages([]);
        setShowAddActivityForm(false);
        setError(null);
        setMessage('');
        setIsLoading(false);
    };

    const handleAddActivity = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setMessage('');
        try {
            const activityData = { ...newActivity, images: [] };
            
            if (imageFiles.length > 0) {
                const storage = getStorage();
                const imageUrls = [];
                for (const file of imageFiles) {
                    const imagePath = `${STORAGE_PATHS.ACTIVITY_IMAGES || 'activity-images'}/${Date.now()}_${file.name}`;
                    const imageRef = storageRef(storage, imagePath);
                    await uploadBytes(imageRef, file);
                    const downloadURL = await getDownloadURL(imageRef);
                    imageUrls.push(downloadURL);
                }
                activityData.images = imageUrls;
            }

            await activitiesAPI.createActivity(activityData);
            setMessage('Activity added successfully!');
            fetchActivitiesInternal();
            resetActivityForm();
        } catch (err) {
            setError(errorHandler.processError(err).message);
            setMessage('');
            setIsLoading(false);
        }
    };

    const handleDeleteActivity = async (id) => {
        if (window.confirm('Are you sure you want to delete this activity?')) {
            try {
                await activitiesAPI.deleteActivity(id);
                fetchActivitiesInternal();
                setMessage('Activity deleted successfully.');
            } catch (err) {
                setError(errorHandler.processError(err).message);
            }
        }
    };

    const handleEditEvent = (event) => {
        setEditingEvent({...event, date: event.date.split('T')[0]});
        setShowEditEventForm(true);
        setShowAddEventForm(false);
        setError(null);
        setMessage('');
    };

    const handleEditActivity = (activity) => {
        setEditingActivity({...activity, date: activity.date.split('T')[0]});
        setShowEditActivityForm(true);
        setShowAddActivityForm(false);
        setError(null);
        setMessage('');
    };

    const handleEventFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                alert('File size must be less than 5MB');
                setError('File size must be less than 5MB. Please choose a smaller file.');
                e.target.value = null;
                return;
            }
            if (!file.type.startsWith('image/')) {
                alert('Please upload an image file (e.g., JPG, PNG, GIF)');
                setError('Invalid file type. Please upload an image.');
                e.target.value = null;
                return;
            }
            setEventImageFile(file);
            if (previewEventImage) {
                URL.revokeObjectURL(previewEventImage);
            }
            setPreviewEventImage(URL.createObjectURL(file));
            setError(null); 
        }
    };

    const handleRemoveEventImage = () => {
        setEventImageFile(null);
        if (previewEventImage) {
            URL.revokeObjectURL(previewEventImage);
        }
        setPreviewEventImage(null);
        const fileInput = document.getElementById('eventImageModalInput');
        if (fileInput) {
            fileInput.value = null;
        }
    };

    const handleAddEvent = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setMessage('');
        try {
            if (!newEvent.title || !newEvent.date || !newEvent.country || !newEvent.city) {
                throw new Error("Please fill in all required fields for the event.");
            }

            let uploadedImageUrl = newEvent.imageUrl || '';

            if (eventImageFile) {
                const storage = getStorage();
                const imagePath = `${STORAGE_PATHS.EVENT_IMAGES || 'event-images'}/${Date.now()}_${eventImageFile.name}`;
                const imageRef = storageRef(storage, imagePath);
                await uploadBytes(imageRef, eventImageFile);
                uploadedImageUrl = await getDownloadURL(imageRef);
            }
            
            const eventData = { 
                ...newEvent, 
                imageUrl: uploadedImageUrl || null
            };
            
            delete eventData.eventImageFile;

            await eventsAPI.createEvent(eventData);
            setMessage('Event added successfully!');
            fetchUpcomingEventsInternal();
            resetEventForm();
        } catch (err) {
            setError(errorHandler.processError(err).message);
            setMessage('');
            setIsLoading(false);
        }
    };

    const resetEventForm = () => {
        setNewEvent({
            title: '',
            description: '',
            date: '',
            country: '',
            city: '',
            requirements: '',
            registrationLink: '',
            imageUrl: ''
        });
        if (previewEventImage) {
            URL.revokeObjectURL(previewEventImage);
        }
        setPreviewEventImage(null);
        setEventImageFile(null);
        const fileInput = document.getElementById('eventImageModalInput');
        if (fileInput) {
            fileInput.value = null;
        }
        setShowAddEventForm(false);
        setError(null);
        setMessage('');
        setIsLoading(false);
    };

    const handleDeleteEvent = async (eventId) => {
        if (window.confirm('Are you sure you want to delete this event?')) {
            try {
                await eventsAPI.deleteEvent(eventId);
                fetchUpcomingEventsInternal();
                setMessage('Event deleted successfully.');
            } catch (err) {
                setError(errorHandler.processError(err).message);
            }
        }
    };

    const getPastActivities = () => {
        return activities.filter(activity => new Date(activity.date) < new Date());
    };
    
    const getUpcomingActivities = () => {
        return activities.filter(activity => new Date(activity.date) >= new Date());
    };

    const handleActivityUpdated = async () => {
        fetchActivitiesInternal();
        setShowEditActivityForm(false);
        setMessage('Activity updated successfully.');
    };

    const handleEventUpdated = async () => {
        fetchUpcomingEventsInternal();
        setShowEditEventForm(false);
        setMessage('Event updated successfully.');
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };

    const openAddActivityModal = () => {
        setMessage('');
        setError(null);
        setShowAddActivityForm(true);
    };

    const openAddEventModal = () => {
        setMessage('');
        setError(null);
        setShowAddEventForm(true);
    };

    const handleScroll = (direction) => {
        if (eventsGridRef.current) {
            const scrollAmount = 500;
            const currentScroll = eventsGridRef.current.scrollLeft;
            eventsGridRef.current.scrollTo({
                left: direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const sliderSettings = {
        dots: true,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: false,
        pauseOnHover: true,
        centerMode: true,
        centerPadding: '60px',
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    centerMode: false,
                    centerPadding: '0'
                }
            }
        ]
    };

    // Sort events by date
    const sortedEvents = [...upcomingEvents].sort((a, b) => new Date(a.date) - new Date(b.date));

    if (pageLoading && activities.length === 0 && upcomingEvents.length === 0) {
        return <div className="activities-page__loading"><LoadingSpinner /></div>;
    }

    return (
        <div className="activities-page">
            <header className="activities-page__header">
                <div className="activities-page__header-content">
                    <h1 className="activities-page__title">Activities & Events</h1>
                    <p className="activities-page__description">
                        Explore our past activities and upcoming events. Join us in making a difference in our community.
                    </p>
                    {(isAdmin || isEditor) && (
                        <div className="admin-actions">
                            <button onClick={openAddActivityModal} className="activities-page__add-button">
                                <i className="fas fa-plus" style={{marginRight: '8px'}}></i> Add Activity
                            </button>
                            <button onClick={openAddEventModal} className="activities-page__add-button">
                                <i className="fas fa-calendar-plus" style={{marginRight: '8px'}}></i> Add Event
                            </button>
                        </div>
                    )}
                </div>
            </header>

            {error && (
                 <div className="activities-page__error" style={{maxWidth: '800px', margin: '1rem auto'}}>
                    <p className="activities-page__error-message">{error}</p>
                    <button 
                        className="activities-page__retry-button"
                        onClick={() => { setRetryCount(prev => prev + 1); setError(null);}}
                    >
                        Retry
                    </button>
                </div>
            )}
            {message && <div className="success-message page-message" style={{maxWidth: '800px', margin: '1rem auto', padding: '1rem', textAlign: 'center'}}>{message}</div>}

            {isAdmin && showAddActivityForm && (
                <AddActivityModal
                    isOpen={showAddActivityForm}
                    onClose={resetActivityForm} 
                    newActivity={newActivity}
                    handleInputChange={handleActivityFormInputChange}
                    handleFileChange={handleActivityFileChange}
                    handleCountryChange={handleActivityCountryChange}
                    handleStateChange={handleActivityStateChange}
                    handleRemoveImage={handleActivityRemoveImage}
                    handleAddActivity={handleAddActivity}
                    previewImages={previewImages}
                    isLoading={isLoading} 
                    error={error} 
                    message={message} 
                />
            )}

            {isAdmin && showAddEventForm && (
                <AddEventModal
                    isOpen={showAddEventForm}
                    onClose={resetEventForm} 
                    newEvent={newEvent}
                    setNewEvent={setNewEvent}
                    handleAddEvent={handleAddEvent}
                    isLoading={isLoading} 
                    error={error} 
                    message={message}
                    eventImageFile={eventImageFile}
                    previewEventImage={previewEventImage}
                    handleEventFileChange={handleEventFileChange}
                    handleRemoveEventImage={handleRemoveEventImage}
                />
            )}
            
            {showEditActivityForm && editingActivity && (
                <EditActivityForm 
                    activity={editingActivity} 
                    onClose={() => { setShowEditActivityForm(false); setEditingActivity(null); }}
                    onActivityUpdated={handleActivityUpdated} 
                />
            )}

            {showEditEventForm && editingEvent && (
                <EditEventForm 
                    event={editingEvent} 
                    onClose={() => { setShowEditEventForm(false); setEditingEvent(null); }}
                    onEventUpdated={handleEventUpdated} 
                />
            )}

            {pageLoading && (activities.length > 0 || upcomingEvents.length > 0) && <div style={{textAlign: 'center', padding: '2rem'}}><LoadingSpinner /></div> }

            {upcomingEvents.length > 0 && (
                <section className="activities-page__section">
                    <h2 className="activities-page__section-title">Upcoming Events</h2>
                    <div className="events-section">
                        <div className="events-slider-wrapper">
                            <Slider {...sliderSettings}>
                                {sortedEvents.map(event => (
                                    <div key={event.id || event.title} className="activity-card event-card">
                                        <div className="activity-card__media">
                                            <img src={event.imageUrl || 'https://via.placeholder.com/300x200?text=Event'} alt={event.title} className="activity-card__image" />
                                            <Countdown targetDate={event.date} />
                                        </div>
                                        <div className="activity-card__content">
                                            <h3 className="activity-card__title">{event.title}</h3>
                                            <p className="activity-card__description">
                                                {event.description}
                                            </p>
                                            <div className="activity-card__meta">
                                                <div className="activity-card__meta-row">
                                                    <span className="activity-card__meta-item">
                                                        <i className="fas fa-calendar"></i>
                                                        {new Date(event.date).toLocaleDateString()}
                                                    </span>
                                                    <span className="activity-card__meta-item">
                                                        <i className="fas fa-map-marker-alt"></i>
                                                        {`${event.city || ''}${event.city && event.country ? ', ' : ''}${event.country ? getCountryName(event.country) : ''}`}
                                                    </span>
                                                </div>
                                            </div>
                                            {event.requirements && (
                                                <>
                                                    <h4>Requirements:</h4>
                                                    <div className="activities-event-requirements">
                                                        <ul className="activities-requirements-list">
                                                            {typeof event.requirements === 'string' && event.requirements.split('\n').filter(req => req.trim()).map((requirement, index) => (
                                                                <li key={index}>
                                                                    <i className="fas fa-check-circle"></i>
                                                                    {requirement.trim()}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </>
                                            )}
                                            {event.partners && <EventPartners partnerIds={event.partners} />}
                                            <div className="activity-card__actions">
                                                <button 
                                                    className="activity-card__button activity-card__button--view-details"
                                                    onClick={() => navigate(`/events/${event.id}`)}
                                                >
                                                    View Details
                                                </button>
                                                {event.registrationLink && 
                                                    <a 
                                                        href={event.registrationLink} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer" 
                                                        className="activity-card__button activity-card__button--register"
                                                    >
                                                        Register Here
                                                    </a>
                                                }
                                                {(isAdmin || isEditor) && (
                                                    <>
                                                        <button onClick={() => handleEditEvent(event)} className="activity-card__button activity-card__button--edit">Edit</button>
                                                        {isSuperAdmin && (
                                                            <button onClick={() => handleDeleteEvent(event.id)} className="activity-card__button activity-card__button--delete">Delete</button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </Slider>
                        </div>
                    </div>
                </section>
            )}
            {!pageLoading && !error && upcomingEvents.length === 0 && (
                <section className="activities-page__section"><p style={{textAlign: 'center'}}>No upcoming events at the moment. Stay tuned!</p></section>
            )}

            {/* Separator between Upcoming Events and All Activities sections */}
            {(upcomingEvents.length > 0 || (!pageLoading && !error && upcomingEvents.length === 0)) && (
                <div className="activities-page__section-separator"></div>
            )}

            <section className="activities-page__section">
                <h2 className="activities-page__section-title">Past Activities</h2>
                {activities.length > 0 && (
                    <div className="activities-page__grid">
                        {activities.map((activity) => (
                            <div key={activity.id} className="activity-card">
                                <div className="activity-card__media">
                                    {activity.images && activity.images.length > 0 ? (
                                        <img
                                            src={activity.images[0]}
                                            alt={activity.title}
                                            className="activity-card__image"
                                        />
                                    ) : (
                                        <img
                                            src="https://via.placeholder.com/300x200?text=Activity"
                                            alt={activity.title}
                                            className="activity-card__image"
                                        />
                                    )}
                                    <div className={`activity-card__status activity-card__status--${activity.status}`}>
                                        {activity.status}
                                    </div>
                                </div>
                                <div className="activity-card__content">
                                    <h3 className="activity-card__title">{activity.title}</h3>
                                    <p className="activity-card__description">{activity.description}</p>
                                    <div className="activity-card__meta">
                                        <div className="activity-card__meta-row">
                                            <div className="activity-card__meta-item">
                                                <i className="fas fa-calendar"></i>
                                                <span>{activity.date}</span>
                                            </div>
                                            <div className="activity-card__meta-item">
                                                <i className="fas fa-map-marker-alt"></i>
                                                <span>{activity.location}</span>
                                            </div>
                                        </div>
                                    </div>
                                    {activity.requirements && (
                                        <div className="activity-card__requirements">
                                            <h4>Requirements</h4>
                                            <ul className="activity-card__requirements-list">
                                                {activity.requirements.map((req, index) => (
                                                    <li key={index}>
                                                        <i className="fas fa-check"></i>
                                                        {req}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <div className="activity-card__actions">
                                        {activity.registrationLink && 
                                            <a href={activity.registrationLink} target="_blank" rel="noopener noreferrer" className="activity-card__button activity-card__button--register">
                                                Register Here
                                            </a>
                                        }
                                        <button 
                                            className="activity-card__button activity-card__button--view-details" 
                                            onClick={() => navigate(`/activities/${activity.id}`)}
                                            style={{ width: '100%', textAlign: 'center' }}
                                        >
                                            View Details
                                        </button>
                                        {(isAdmin || isEditor) && (
                                            <>
                                                <button 
                                                    onClick={() => handleEditActivity(activity)} 
                                                    className="activity-card__button activity-card__button--edit"
                                                >
                                                    Edit
                                                </button>
                                                {isSuperAdmin && (
                                                    <button 
                                                        onClick={() => handleDeleteActivity(activity.id)} 
                                                        className="activity-card__button activity-card__button--delete"
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {!pageLoading && !error && activities.length === 0 && (
                     <p style={{textAlign: 'center'}}>No activities found. {isAdmin && "Add one using the button above!"}</p>
                )}
            </section>

            {!pageLoading && !error && activities.length > 0 && pagination.totalPages > 1 && (
                 <div className="activities-page__pagination">
                    <button
                        className="activities-page__pagination-button"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                    >
                        Previous
                    </button>
                    <span className="activities-page__pagination-info">
                        Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button
                        className="activities-page__pagination-button"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages || pagination.page >= pagination.totalPages }
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}

export default Activities;
